import { AfterContentInit, Directive, Input, OnDestroy } from '@angular/core'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { BrandingService } from '../../../../injectable/branding-service'
import { ConsumerViewService } from '../../../../injectable/consumer-view-service'

interface BrandLogos {
    square: string;
    rectangle: string;
}

export enum LogoLayout {
    Rectangle = 'rectangle',
    Square = 'square'
}

export enum LogoSize {
    Small = 'small',
    Medium = 'medium',
    Large = 'large',
    XLarge = 'xlarge'
}

@Directive({
    host: {
        '[attr.src]': 'src',
        '[attr.alt]': 'alt',
        '[class]': 'sizeClass'
    },
    selector: '[app-logo]'
})
export class LogoDirective implements AfterContentInit, OnDestroy {
    @Input() layout: LogoLayout = LogoLayout.Rectangle
    @Input() size: LogoSize = LogoSize.Medium

    src: string | SafeUrl
    alt: string
    sizeClass: string
    
    private destroy$ = new Subject<void>();

    private readonly sizeMatrix = {
        [LogoLayout.Rectangle]: {
            [LogoSize.Small]: '_h-3',
            [LogoSize.Medium]: '_h-5',
            [LogoSize.Large]: '_h-7',
            [LogoSize.XLarge]: '_h-10'
        },
        [LogoLayout.Square]: {
            [LogoSize.Small]: '_h-6',
            [LogoSize.Medium]: '_h-8',
            [LogoSize.Large]: '_h-12',
            [LogoSize.XLarge]: '_h-15'
        }
    }

    constructor(
        private _brandingService: BrandingService,
        private _consumerViewService: ConsumerViewService,
        private _sanitizer: DomSanitizer
    ) {}

    ngAfterContentInit() {
        console.log('LogoDirective: Initializing with layout:', this.layout, 'size:', this.size);
        
        // Get initial logos
        const initialLogos = this._brandingService.getLogos();
        this.updateLogo(initialLogos);
        
        // Subscribe to logo changes
        this._brandingService.logos$
            .pipe(
                takeUntil(this.destroy$)
            )
            .subscribe(logos => {
                console.log('LogoDirective: Received logos update:', logos);
                this.updateLogo(logos);
            })
            
        this._consumerViewService.consumerViewSubject
            .pipe(
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                const conusmerViewLogo = this._brandingService.getLogos()
                this.updateLogo(conusmerViewLogo);
            })
            
        this.sizeClass = this.sizeMatrix[this.layout][this.size];
        console.log('LogoDirective: Set size class:', this.sizeClass);
    }

    private updateLogo(logos: BrandLogos | null) {
        if (!logos) {
            console.log('LogoDirective: No logos available yet');
            this.src = '';
            this.alt = 'Loading logo...';
            return;
        }
        
        const logoUrl = this.layout === LogoLayout.Square ? logos.square : logos.rectangle;
        console.log('LogoDirective: Setting logo URL:', logoUrl, 'for layout:', this.layout);
        
        // Sanitize if it's a data URL
        this.src = logoUrl.startsWith('data:')
            ? this._sanitizer.bypassSecurityTrustUrl(logoUrl)
            : logoUrl;
            
        // Set alt text
        this.alt = 'Company Logo';
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
